/**
 * Os planos de ação da empresa em contexto (issue #359).
 *
 * A empresa pode ter mais de um: o do ciclo corrente e os anteriores, que ficam guardados com
 * os prazos que tinham. A tarefa desta tela é abrir o plano certo — criar é o caso raro, e por
 * isso o cadastro é um diálogo aqui mesmo, com cinco campos, e não uma página.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { ActionButton, DataTable, PageShell, type DataTableColumn } from '../../components';
import { useAcao } from '../../authorization/useVeredito';
import { actionPlanService } from '../../services/actionPlanService';
import {
  ROTULO_DA_SITUACAO_DO_PLANO,
  SITUACOES_DO_PLANO,
  formatarDia,
  type ActionPlanStatus,
  type ActionPlanSummary,
  type DadosDoPlano,
} from '../../types/actionPlan';

/** O dia de hoje em `YYYY-MM-DD`, que é como a data de elaboração viaja. */
function hojeEmTexto(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

function planoVazio(): DadosDoPlano {
  return { title: '', elaboratedAt: hojeEmTexto(), status: 'VIGENTE', notes: null };
}

const colunas: DataTableColumn<ActionPlanSummary>[] = [
  { id: 'title', label: 'Plano', primary: true, render: (p) => p.title },
  {
    id: 'elaboratedAt',
    label: 'Elaborado em',
    render: (p) => formatarDia(p.elaboratedAt),
  },
  {
    id: 'status',
    label: 'Situação',
    render: (p) => (
      <Chip
        size="small"
        variant="outlined"
        color={p.status === 'VIGENTE' ? 'success' : 'default'}
        label={ROTULO_DA_SITUACAO_DO_PLANO[p.status]}
      />
    ),
  },
  { id: 'totalDeAcoes', label: 'Ações', align: 'right', render: (p) => p.totalDeAcoes },
  {
    id: 'acoesVencidas',
    label: 'Vencidas',
    align: 'right',
    render: (p) =>
      p.acoesVencidas > 0 ? (
        <Chip size="small" color="error" label={p.acoesVencidas} />
      ) : (
        <span>{p.acoesVencidas}</span>
      ),
  },
];

export const ActionPlanListPage: React.FC = () => {
  const navigate = useNavigate();
  const criar = useAcao('action_plan.create', 'Novo plano');
  const editar = useAcao('action_plan.update', 'Editar plano');
  const excluir = useAcao('action_plan.delete', 'Excluir plano');

  const [planos, setPlanos] = useState<ActionPlanSummary[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [emEdicao, setEmEdicao] = useState<ActionPlanSummary | null>(null);
  const [aberto, setAberto] = useState(false);
  const [dados, setDados] = useState<DadosDoPlano>(planoVazio);
  const [salvando, setSalvando] = useState(false);
  const [aExcluir, setAExcluir] = useState<ActionPlanSummary | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      setPlanos(await actionPlanService.listarPlanos());
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível carregar os planos de ação');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const abrirNovo = useCallback(() => {
    setEmEdicao(null);
    setDados(planoVazio());
    setAberto(true);
  }, []);

  const abrirEdicao = useCallback((plano: ActionPlanSummary) => {
    setEmEdicao(plano);
    setDados({
      title: plano.title,
      elaboratedAt: plano.elaboratedAt,
      status: plano.status,
      notes: plano.notes,
    });
    setAberto(true);
  }, []);

  const salvar = useCallback(async () => {
    if (dados.title.trim() === '' || dados.elaboratedAt === '') {
      setErro('O título e a data de elaboração são o que identificam o plano.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      if (emEdicao) {
        await actionPlanService.atualizarPlano(emEdicao.id, dados);
        setAberto(false);
        await carregar();
        return;
      }

      // Quem acaba de criar o plano vem cadastrar as ações, e isso só acontece na tela dele.
      const criado = await actionPlanService.criarPlano(dados);
      setAberto(false);
      navigate(`/action-plan/${criado.id}`);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível salvar o plano de ação');
    } finally {
      setSalvando(false);
    }
  }, [carregar, dados, emEdicao, navigate]);

  const confirmarExclusao = useCallback(async () => {
    if (!aExcluir) return;
    try {
      await actionPlanService.removerPlano(aExcluir.id);
      setAExcluir(null);
      await carregar();
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível excluir o plano de ação');
    }
  }, [aExcluir, carregar]);

  const mudouAElaboracao = emEdicao !== null && dados.elaboratedAt !== emEdicao.elaboratedAt;

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Plano de Ação' }]}
      title="Plano de Ação"
      subtitle="O que a empresa vai fazer para tratar cada risco, com prazo e responsável"
      actions={
        <ActionButton
          acao="action_plan.create"
          rotulo="Novo plano"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={abrirNovo}
          disabled={criar.disabled}
        >
          Novo plano
        </ActionButton>
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro(null)}>
          {erro}
        </Alert>
      )}

      <DataTable
        columns={colunas}
        rows={planos}
        getRowId={(plano) => plano.id}
        loading={carregando}
        onRowClick={(plano) => navigate(`/action-plan/${plano.id}`)}
        emptyState={{
          title: 'Nenhum plano de ação nesta empresa ainda',
          description:
            'Um plano de ação reúne o que a empresa vai fazer para tratar cada risco, com prazo e responsável — e sai como quadro no PGR. Comece por "Novo plano".',
        }}
        actions={[
          {
            icon: <EditIcon fontSize="small" />,
            color: 'primary',
            onClick: abrirEdicao,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: (plano: ActionPlanSummary) => setAExcluir(plano),
            ...excluir,
          },
        ]}
      />

      <Dialog open={aberto} onClose={() => setAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>{emEdicao ? 'Editar plano' : 'Novo plano de ação'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              required
              label="Título"
              placeholder="Plano de ação — PGR 2026"
              value={dados.title}
              onChange={(e) => setDados({ ...dados, title: e.target.value })}
            />
            <TextField
              fullWidth
              required
              type="date"
              label="Data de elaboração do PGR"
              helperText="É dela que sai o prazo de cada ação"
              InputLabelProps={{ shrink: true }}
              value={dados.elaboratedAt}
              onChange={(e) => setDados({ ...dados, elaboratedAt: e.target.value })}
            />
            {mudouAElaboracao && (
              <Alert severity="info">
                Ao salvar, o prazo das ações ainda não concluídas deste plano é recalculado — são{' '}
                {emEdicao?.totalDeAcoes ?? 0} ações no total, e as já concluídas mantêm o prazo que
                tinham.
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>Situação</InputLabel>
              <Select
                label="Situação"
                value={dados.status}
                onChange={(e) =>
                  setDados({ ...dados, status: e.target.value as ActionPlanStatus })
                }
              >
                {SITUACOES_DO_PLANO.map((situacao) => (
                  <MenuItem key={situacao} value={situacao}>
                    {ROTULO_DA_SITUACAO_DO_PLANO[situacao]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Observação"
              value={dados.notes ?? ''}
              onChange={(e) =>
                setDados({ ...dados, notes: e.target.value === '' ? null : e.target.value })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAberto(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => void salvar()} disabled={salvando}>
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={aExcluir !== null} onClose={() => setAExcluir(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir o plano {aExcluir?.title}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {aExcluir?.totalDeAcoes === 0
              ? 'O plano ainda não tem ações — some sozinho.'
              : `As ${aExcluir?.totalDeAcoes ?? 0} ações deste plano vão junto, com as evidências anexadas a elas.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAExcluir(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => void confirmarExclusao()}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default ActionPlanListPage;
